import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Info } from 'lucide-react';
import { approvalsDashboardAPI, type DashRichFilterParams } from '../../services/api';
import { buildCsv, downloadCsv } from '../../utils/csv';
import { Modal, SearchBox, tonedCell } from './DashboardPrimitives';
import { BlockLoader } from '../loading';
import { useTableSort, SortTh, type SortAccessors } from './useTableSort';
import StatTile from './StatTile';
import { useDashboardList } from './useDashboardList';
import { APPROVALS_LIST, approvalsListFromSlug } from './lists/approvals';

/**
 * Dashboard → Approvals tab.
 *
 * The analytics counterpart to the My Approvals page: who raises requests, whose
 * queue they land in, and how they were decided — across leave, attendance
 * regularization and market regularization.
 *
 * Two things a reader must understand, both surfaced in the UI rather than left
 * to a code comment (see the footnote rendered at the bottom):
 *   1. "Manager" is the EXPECTED approver — the employee's current reporting
 *      manager — not whoever actually clicked Approve. Upstream never returns an
 *      actioner on the read path.
 *   2. A month is a COHORT of requests RAISED in it, shown with their status as of
 *      now. So a past month's Pending can shrink on a later visit.
 *
 * Deliberately no day-wise trend chart: request volume per day is noisy and
 * carries no decision, whereas the per-manager and per-type splits do.
 */

type ReqType = 'leave' | 'attendance_regularization' | 'market_regularization';
type ReqStatus = 'pending' | 'approved' | 'rejected';

interface TypeCounts { leave: number; attendance_regularization: number; market_regularization: number }
interface Bucket { total: number; pending: number; approved: number; rejected: number; approvalRate: number | null }

interface ApprovalRow {
  id: string;
  type: ReqType;
  typeLabel: string;
  leaveType: string | null;
  employeeCode: string;
  employeeName: string | null;
  city: string | null;
  /** YYYY-MM-DD, or null when upstream carries no joining date. */
  joiningDate: string | null;
  managerCode: string | null;
  managerName: string | null;
  /** Nearest Assistant-Manager-role / Trade-Head-role holder above the employee — the
   *  AM / Trade Head tables' grouping key. Absent from an API older than this build. */
  amCode?: string | null;
  amName?: string | null;
  thCode?: string | null;
  thName?: string | null;
  status: ReqStatus;
  appliedOn: string | null;
  // The four TAT fields are optional: an API older than this build omits them,
  // and every reader below treats absent exactly like unknown.
  /** 'YYYY-MM-DD HH:mm' as 1Office sent it — the moment the request was applied. */
  appliedAt?: string | null;
  /** When it was decided, same format. Leave only, and only when 1Office reports it. */
  decidedAt?: string | null;
  /** Pending only — whole calendar days since it was applied (0 = today). */
  pendingDays?: number | null;
  /** Decided only — calendar days from applying to the decision. Null when the decision date is unknown. */
  tatDays?: number | null;
  referenceDate: string | null;
  reason: string | null;
}

interface ManagerRow extends Bucket {
  managerCode: string | null;
  managerName: string;
  byType: TypeCounts;
  /** Pending TAT across this row's still-pending requests, in calendar days since each
   *  was applied: the average and the longest wait. Null when nothing is pending;
   *  absent from an API older than this build. */
  avgPendingDays?: number | null;
  maxPendingDays?: number | null;
}
interface TypeRow extends Bucket { type: ReqType; label: string }
interface LeaveRow extends Bucket { leaveType: string }
interface RequesterRow extends Bucket {
  employeeCode: string;
  employeeName: string | null;
  city: string | null;
  joiningDate: string | null;
  managerName: string | null;
  byType: TypeCounts;
}

interface DashData {
  month: { ym: string; label: string; startDate: string; endDate: string };
  supported: boolean;
  /** An upstream fetch failed — the numbers below are an undercount. */
  partial: boolean;
  notice: string | null;
  kpis: Bucket & { decided: number };
  byManager: ManagerRow[];
  /** By Assistant Manager / by Trade Head — only populated when tiers.available; a
   *  project defining neither role keeps the By manager table. */
  byAm?: ManagerRow[];
  byTh?: ManagerRow[];
  tiers?: { available: boolean };
  byType: TypeRow[];
  leaveBreakdown: LeaveRow[];
  topRequesters: RequesterRow[];
  rows: ApprovalRow[];
}

const pct = (v: number | null): string => (v == null ? '—' : `${v}%`);

/** MUST match the backend's leave-breakdown bucket key exactly — it trims before
 *  grouping, so comparing the raw value here would make every drill-down on a
 *  leave type whose label carries stray whitespace open empty. */
const normLeaveType = (v: string | null): string => (v || 'Unspecified').trim() || 'Unspecified';

const STATUS_LABEL: Record<ReqStatus, string> = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };

/** Status chip. Only the exception (rejected) is coloured red; pending is amber
 *  because it is actionable. Approved stays neutral — it is the normal outcome. */
function StatusChip({ status }: { status: ReqStatus }) {
  const cls =
    status === 'rejected' ? 'bg-rose-50 text-rose-700'
    : status === 'pending' ? 'bg-amber-50 text-amber-700'
    : 'bg-gray-100 text-gray-600';
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{STATUS_LABEL[status]}</span>;
}

function Card({ title, subtitle, action, children }: { title: string; subtitle?: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// Headers stay on one line: a narrow table scrolls inside its card rather than
// stacking "Approval" over "rate".
const TH = 'whitespace-nowrap px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500';
const THR = 'whitespace-nowrap px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500';
const TD = 'px-3 py-2 text-sm text-gray-700';
const TDR = 'px-3 py-2 text-right text-sm tabular-nums text-gray-700';

/** Tighter numeric columns for the requester table only, which carries seven count
 *  columns after four about the person. */
const THRC = 'whitespace-nowrap px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500';
const TDRC = 'px-2 py-2 text-right text-sm tabular-nums text-gray-700';

/** 'YYYY-MM-DD' → '12 Mar 2024'. Built from the string parts rather than `new Date`,
 *  which would re-interpret a bare date in the viewer's timezone and can shift it a
 *  day west of UTC. Falls back to the raw value if it isn't the expected shape. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(iso);
  const mon = MONTHS[Number(m[2]) - 1];
  return mon ? `${Number(m[3])} ${mon} ${m[1]}` : String(iso);
};

/** 'YYYY-MM-DD HH:mm' → '2:05 pm', or null when the stamp has no time. Read off
 *  the string like fmtDate, so it shows the wall-clock 1Office recorded. */
const fmtTime = (stamp: string | null | undefined): string | null => {
  const m = String(stamp ?? '').match(/^\d{4}-\d{2}-\d{2}[ T](\d{2}):(\d{2})/);
  if (!m) return null;
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'am' : 'pm'}`;
};

const plural = (n: number, unit: string): string => `${n} ${unit}${n === 1 ? '' : 's'}`;

/** TAT cell text: how long a pending request has waited, or how long a decided
 *  one took. '—' when unknown. */
const fmtTat = (r: ApprovalRow): string => {
  if (r.status === 'pending') {
    if (r.pendingDays == null) return '—';
    return r.pendingDays === 0 ? 'Today' : plural(r.pendingDays, 'day');
  }
  if (r.tatDays == null) return '—';
  return r.tatDays === 0 ? 'Same day' : plural(r.tatDays, 'day');
};

const TAT_UNKNOWN_HINT = "1Office doesn't send a decision date for this request";

/** A roll-up's pending TAT — '3.5 days', '1 day'. '—' when nothing is pending. */
const fmtPendingTat = (days: number | null | undefined): string => (days == null ? '—' : plural(days, 'day'));

type StatusFilter = 'all' | ReqStatus;
const STATUS_FILTERS: Array<{ key: StatusFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

interface DrillState {
  /** Bumped on every open, so reopening always starts from a fresh modal. */
  id: number;
  title: string;
  rows: ApprovalRow[];
  /** The status the modal opens on — the Pending card opens on Pending, and so on. */
  status: StatusFilter;
  /** A manager's own drill: every row shares that manager, so the column would only repeat the title. */
  hideManager?: boolean;
}

/** A count that opens the drill-down on that status. Zero stays plain text —
 *  there is nothing to list. Stops propagation so the row's own click (which
 *  opens every status) doesn't fire as well. `children` shows something other
 *  than the count — the pending-TAT cells show days but open the pending list. */
function CountCell({ value, className, onOpen, children }: { value: number; className: string; onOpen: () => void; children?: React.ReactNode }) {
  return (
    <td className={className}>
      {value > 0 ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          className="rounded tabular-nums underline-offset-2 hover:text-primary-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          {children ?? value}
        </button>
      ) : (children ?? value)}
    </td>
  );
}

/** Rows rendered per step. A month-wide drill on a large project is thousands of
 *  requests; rendering them all at once makes the modal, and every re-sort, lag. */
const DRILL_PAGE = 200;

const DRILL_SORT: SortAccessors<ApprovalRow> = {
  emp: (r) => r.employeeName || r.employeeCode,
  type: (r) => r.typeLabel,
  manager: (r) => r.managerName,
  // Raw stamps, not the display strings, so both date columns order chronologically.
  applied: (r) => r.appliedAt ?? r.appliedOn,
  refDate: (r) => r.referenceDate,
  status: (r) => STATUS_LABEL[r.status],
  // One number per row: waiting time for pending, decision time otherwise.
  tat: (r) => (r.status === 'pending' ? r.pendingDays : r.tatDays) ?? null,
  reason: (r) => r.reason,
};

const fileSlug = (s: string): string => s.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);

/**
 * The drill-down behind every entry point — KPI cards, count cells and rows. Status
 * chips re-slice the same rows, so a manager's row can be narrowed to what is still
 * pending with them without closing and reopening.
 *
 * `onStatus` is given for the month's list, whose status is named in the address: a chip
 * then writes the address and the status shown is read back from `drill.status`, so the
 * address always says what is listed. Every other drill keeps its status to itself.
 */
function DrillDownModal({ drill, ym, onClose, onStatus }: { drill: DrillState; ym: string; onClose: () => void; onStatus?: (status: StatusFilter) => void }) {
  const [ownStatus, setOwnStatus] = useState<StatusFilter>(drill.status);
  const status = onStatus ? drill.status : ownStatus;
  const setStatus = onStatus ?? setOwnStatus;
  const [search, setSearch] = useState('');

  const counts = useMemo(() => {
    const c: Record<ReqStatus, number> = { pending: 0, approved: 0, rejected: 0 };
    for (const r of drill.rows) c[r.status] += 1;
    return c;
  }, [drill.rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return drill.rows.filter((r) =>
      (status === 'all' || r.status === status) &&
      (!q || [r.employeeName, r.employeeCode, r.managerName, r.managerCode, r.typeLabel, r.leaveType, r.reason]
        .some((v) => String(v ?? '').toLowerCase().includes(q))),
    );
  }, [drill.rows, status, search]);

  // Exports exactly what is listed — this status, this search, this sort order.
  const handleDownload = (rows: ApprovalRow[]) => {
    const headers = ['Employee Code', 'Employee', 'City', 'Joining Date', 'Manager Code', 'Manager (expected approver)', 'Request Type', 'Leave Type', 'Status', 'Applied At', 'Request Date', 'Decided At', 'Days Pending', 'TAT (days)', 'Reason'];
    const out = rows.map((r) => [
      r.employeeCode,
      r.employeeName ?? '',
      r.city ?? '',
      r.joiningDate ?? '',
      r.managerCode ?? '',
      r.managerName ?? '',
      r.typeLabel,
      r.leaveType ?? '',
      STATUS_LABEL[r.status],
      // Raw stamps, not the display format — spreadsheets sort and date-parse these.
      r.appliedAt ?? r.appliedOn ?? '',
      r.referenceDate ?? '',
      r.decidedAt ?? '',
      r.status === 'pending' && r.pendingDays != null ? String(r.pendingDays) : '',
      r.status !== 'pending' && r.tatDays != null ? String(r.tatDays) : '',
      r.reason ?? '',
    ]);
    const suffix = status === 'all' ? '' : `_${STATUS_LABEL[status]}`;
    downloadCsv(`Approvals_${ym}_${fileSlug(drill.title)}${suffix}.csv`, buildCsv(headers, out));
  };

  return (
    <Modal title={drill.title} onClose={onClose} maxWidth="max-w-6xl">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => {
          const n = f.key === 'all' ? drill.rows.length : counts[f.key];
          const active = status === f.key;
          return (
            <button
              key={f.key}
              type="button"
              aria-pressed={active}
              disabled={!active && n === 0}
              onClick={() => setStatus(f.key)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${
                active ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label} <span className={active ? 'text-white/80' : 'text-gray-400'}>{n}</span>
            </button>
          );
        })}
      </div>
      {/* Keyed by status so the sort resets to that view's natural order. */}
      <DrillDownTable
        key={status}
        rows={filtered}
        status={status}
        search={search}
        onSearch={setSearch}
        hideManager={!!drill.hideManager}
        onDownload={handleDownload}
      />
    </Modal>
  );
}

function DrillDownTable({
  rows,
  status,
  search,
  onSearch,
  hideManager,
  onDownload,
}: {
  rows: ApprovalRow[];
  status: StatusFilter;
  search: string;
  onSearch: (v: string) => void;
  hideManager: boolean;
  onDownload: (rows: ApprovalRow[]) => void;
}) {
  // Pending is the actionable queue, so it opens oldest-first; every other view
  // keeps the server's newest-first order until a header is clicked.
  const sort = useTableSort(rows, DRILL_SORT, status === 'pending' ? { defaultKey: 'tat', defaultDir: 'desc' } : undefined);
  const [limit, setLimit] = useState(DRILL_PAGE);
  // Back to the first page whenever what is listed, or its order, changes.
  useEffect(() => { setLimit(DRILL_PAGE); }, [rows, sort.sortKey, sort.sortDir]);
  const undated = useMemo(() => rows.filter((r) => r.status !== 'pending' && r.tatDays == null).length, [rows]);
  const shown = sort.sorted.slice(0, limit);
  const remaining = sort.sorted.length - shown.length;

  return (
    <>
      <div className="mb-3 flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <SearchBox value={search} onChange={onSearch} placeholder="Search employee, manager, type or reason…" />
        </div>
        <button
          type="button"
          onClick={() => onDownload(sort.sorted)}
          disabled={rows.length === 0}
          className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" /> Download
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500">No matching requests.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <SortTh label="Employee" sortKey="emp" sort={sort} className={TH} />
                  <SortTh label="Request type" sortKey="type" sort={sort} className={TH} />
                  {!hideManager && (
                    <SortTh
                      label={status === 'pending' ? 'Pending with' : 'Manager'}
                      sortKey="manager"
                      sort={sort}
                      className={TH}
                      title="The employee's current reporting manager — the expected approver"
                    />
                  )}
                  <SortTh label="Applied on" sortKey="applied" sort={sort} className={TH} />
                  <SortTh label="Request date" sortKey="refDate" sort={sort} className={TH} />
                  <SortTh label="Status" sortKey="status" sort={sort} className={TH} />
                  <SortTh
                    label={status === 'pending' ? 'Pending for' : 'TAT'}
                    sortKey="tat"
                    sort={sort}
                    align="right"
                    className={THR}
                    title="Pending: days since it was applied. Decided: days from applying to the decision."
                  />
                  <SortTh label="Reason" sortKey="reason" sort={sort} className={TH} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {shown.map((r) => {
                  const time = fmtTime(r.appliedAt);
                  return (
                    <tr key={r.id}>
                      <td className={TD}>
                        <div className="font-medium text-gray-900">{r.employeeName || '—'}</div>
                        <div className="text-[11px] text-gray-400">{r.employeeCode}</div>
                      </td>
                      <td className={TD}>
                        {r.typeLabel}
                        {r.leaveType && <div className="text-[11px] text-gray-400">{r.leaveType}</div>}
                      </td>
                      {!hideManager && (
                        <td className={TD}>
                          <div className="text-gray-900">{r.managerName || 'Unassigned'}</div>
                          {r.managerCode && <div className="text-[11px] text-gray-400">{r.managerCode}</div>}
                        </td>
                      )}
                      <td className={`${TD} whitespace-nowrap`}>
                        {fmtDate(r.appliedOn)}
                        {time && <div className="text-[11px] text-gray-400">{time}</div>}
                      </td>
                      <td className={`${TD} whitespace-nowrap`}>{fmtDate(r.referenceDate)}</td>
                      <td className={`${TD} whitespace-nowrap`}>
                        <StatusChip status={r.status} />
                        {r.status !== 'pending' && r.decidedAt && (
                          <div className="mt-0.5 text-[11px] text-gray-400">on {fmtDate(r.decidedAt)}</div>
                        )}
                      </td>
                      <td
                        className={`${TDR} whitespace-nowrap`}
                        title={r.status !== 'pending' && r.tatDays == null ? TAT_UNKNOWN_HINT : undefined}
                      >
                        {fmtTat(r)}
                      </td>
                      <td className={`${TD} max-w-xs truncate`} title={r.reason ?? ''}>{r.reason || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {remaining > 0 && (
            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="text-[11px] text-gray-400">
                Showing {shown.length} of {sort.sorted.length}. Download for the full list.
              </span>
              <button
                type="button"
                onClick={() => setLimit((l) => l + DRILL_PAGE)}
                className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
              >
                Show {Math.min(DRILL_PAGE, remaining)} more
              </button>
            </div>
          )}
          {undated > 0 && (
            <p className="mt-2 text-[11px] text-gray-400">
              TAT shows — for {plural(undated, 'decided request')}: 1Office doesn't send a decision date for{' '}
              {undated === 1 ? 'it' : 'them'} (it never does for regularizations).
            </p>
          )}
        </>
      )}
    </>
  );
}

/**
 * One per-person roll-up — By manager, By Assistant Manager or By Trade Head. The three
 * share every column; only the grouping (and the first header) differs. Each instance
 * owns its sort state, so sorting one table never reorders another. `onOpen` gets the
 * clicked row and, for a count cell, the status that count was.
 */
function ManagerBreakdownTable({
  rows,
  nameLabel,
  onOpen,
}: {
  rows: ManagerRow[];
  nameLabel: string;
  onOpen: (r: ManagerRow, status?: StatusFilter) => void;
}) {
  const sort = useTableSort(rows, {
    manager: (r: ManagerRow) => r.managerName,
    total: (r: ManagerRow) => r.total,
    leave: (r: ManagerRow) => r.byType.leave,
    attreg: (r: ManagerRow) => r.byType.attendance_regularization,
    mktreg: (r: ManagerRow) => r.byType.market_regularization,
    pending: (r: ManagerRow) => r.pending,
    avgPendingTat: (r: ManagerRow) => r.avgPendingDays,
    maxPendingTat: (r: ManagerRow) => r.maxPendingDays,
    approved: (r: ManagerRow) => r.approved,
    rejected: (r: ManagerRow) => r.rejected,
    rate: (r: ManagerRow) => r.approvalRate,
  });
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <SortTh label={nameLabel} sortKey="manager" sort={sort} className={TH} />
            <SortTh label="Total" sortKey="total" sort={sort} align="right" className={THR} />
            <SortTh label="Leave" sortKey="leave" sort={sort} align="right" className={THR} />
            <SortTh label="Att. reg." sortKey="attreg" sort={sort} align="right" className={THR} />
            <SortTh label="Market reg." sortKey="mktreg" sort={sort} align="right" className={THR} />
            <SortTh label="Pending" sortKey="pending" sort={sort} align="right" className={THR} />
            <SortTh
              label="Avg pending TAT"
              sortKey="avgPendingTat"
              sort={sort}
              align="right"
              className={THR}
              title="Average number of days the pending requests have been waiting since they were applied"
            />
            <SortTh
              label="Max pending TAT"
              sortKey="maxPendingTat"
              sort={sort}
              align="right"
              className={THR}
              title="Days the longest-waiting pending request has been waiting since it was applied"
            />
            <SortTh label="Approved" sortKey="approved" sort={sort} align="right" className={THR} />
            <SortTh label="Rejected" sortKey="rejected" sort={sort} align="right" className={THR} />
            <SortTh label="Approval rate" sortKey="rate" sort={sort} align="right" className={THR} />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {sort.sorted.map((r) => (
            <tr key={r.managerCode ?? '__none__'} className="cursor-pointer hover:bg-gray-50" onClick={() => onOpen(r)}>
              <td className={TD}>
                <div className="font-medium text-gray-900">{r.managerName}</div>
                {r.managerCode && <div className="text-[11px] text-gray-400">{r.managerCode}</div>}
              </td>
              <td className={`${TDR} font-medium`}>{r.total}</td>
              <td className={TDR}>{r.byType.leave}</td>
              <td className={TDR}>{r.byType.attendance_regularization}</td>
              <td className={TDR}>{r.byType.market_regularization}</td>
              <CountCell value={r.pending} className={tonedCell(TDR, r.pending > 0 && 'font-medium text-amber-700')} onOpen={() => onOpen(r, 'pending')} />
              {/* Both open the pending list, which lists oldest first. */}
              <CountCell value={r.pending} className={`${TDR} whitespace-nowrap`} onOpen={() => onOpen(r, 'pending')}>
                {fmtPendingTat(r.avgPendingDays)}
              </CountCell>
              <CountCell value={r.pending} className={`${TDR} whitespace-nowrap`} onOpen={() => onOpen(r, 'pending')}>
                {fmtPendingTat(r.maxPendingDays)}
              </CountCell>
              <CountCell value={r.approved} className={TDR} onOpen={() => onOpen(r, 'approved')} />
              <CountCell value={r.rejected} className={TDR} onOpen={() => onOpen(r, 'rejected')} />
              <td className={TDR}>{pct(r.approvalRate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ApprovalsTab({
  projectId,
  month,
  roleId,
  filters,
}: {
  projectId: number | null;
  month: string;
  roleId?: string;
  filters?: DashRichFilterParams;
}) {
  const [data, setData] = useState<DashData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqRef = useRef(0);

  // Drill-down modal — a pre-filtered slice of `rows`; status and search live in the modal.
  const [drill, setDrill] = useState<DrillState | null>(null);
  const drillSeq = useRef(0);
  // The month's own list (what the four count tiles open) is named in the address
  // instead (?list=all | pending | approved | rejected) and drawn from the rows on
  // screen on every render. It holds no snapshot, so unlike `drill` it stays open
  // across the reload that follows the default Role filter.
  const { list, openList, closeList } = useDashboardList();

  useEffect(() => {
    if (projectId == null) return;
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    // An open drill-down holds a snapshot of the previous rows — close it rather
    // than let it reappear over a different month or filter once this load lands.
    // (The month's own list holds none: it is drawn from the address and the new rows.)
    setDrill(null);
    approvalsDashboardAPI
      .getDashboard({ month, projectId, roleId: roleId || undefined, ...filters })
      .then((res) => {
        if (reqId !== reqRef.current) return;
        setData((res.data?.data ?? null) as DashData | null);
      })
      .catch((err) => {
        if (reqId !== reqRef.current) return;
        setError(err.response?.data?.message || 'Failed to load leave and attendance requests');
        setData(null);
      })
      .finally(() => {
        if (reqId === reqRef.current) setLoading(false);
      });
  }, [month, projectId, roleId, JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps

  const managerRows = data?.byManager ?? [];
  // By Assistant Manager + By Trade Head replace By manager when the project defines
  // those roles (see the controller); otherwise By manager stays, exactly as before.
  const amRows = data?.byAm ?? [];
  const thRows = data?.byTh ?? [];
  const tiersOn = !!data?.tiers?.available;
  const requesterRows = data?.topRequesters ?? [];
  const leaveRows = data?.leaveBreakdown ?? [];
  const typeRows = data?.byType ?? [];

  // Sort hooks must be unconditional — declared before any early return. The per-person
  // roll-ups (By manager / Assistant Manager / Trade Head) sort inside
  // ManagerBreakdownTable instead, one state per table.
  const empSort = useTableSort(requesterRows, {
    emp: (r: RequesterRow) => r.employeeName || r.employeeCode,
    code: (r: RequesterRow) => r.employeeCode,
    city: (r: RequesterRow) => r.city,
    // Raw ISO, not the formatted string — '12 Mar 2024' would sort alphabetically.
    joined: (r: RequesterRow) => r.joiningDate,
    manager: (r: RequesterRow) => r.managerName,
    total: (r: RequesterRow) => r.total,
    leave: (r: RequesterRow) => r.byType.leave,
    attreg: (r: RequesterRow) => r.byType.attendance_regularization,
    mktreg: (r: RequesterRow) => r.byType.market_regularization,
    pending: (r: RequesterRow) => r.pending,
    approved: (r: RequesterRow) => r.approved,
    rejected: (r: RequesterRow) => r.rejected,
  });
  const leaveSort = useTableSort(leaveRows, {
    leaveType: (r: LeaveRow) => r.leaveType,
    total: (r: LeaveRow) => r.total,
    pending: (r: LeaveRow) => r.pending,
    approved: (r: LeaveRow) => r.approved,
    rejected: (r: LeaveRow) => r.rejected,
    rate: (r: LeaveRow) => r.approvalRate,
  });
  const typeSort = useTableSort(typeRows, {
    type: (r: TypeRow) => r.label,
    total: (r: TypeRow) => r.total,
    pending: (r: TypeRow) => r.pending,
    approved: (r: TypeRow) => r.approved,
    rejected: (r: TypeRow) => r.rejected,
    rate: (r: TypeRow) => r.approvalRate,
  });

  const openDrill = (title: string, rows: ApprovalRow[], opts: { status?: StatusFilter; hideManager?: boolean } = {}) => {
    drillSeq.current += 1;
    setDrill({ id: drillSeq.current, title, rows, status: opts.status ?? 'all', hideManager: opts.hideManager });
  };

  const handleDownload = () => {
    if (!data) return;
    const headers = ['Employee Code', 'Employee', 'City', 'Joining Date', 'Manager (expected approver)', 'Request Type', 'Leave Type', 'Status', 'Applied On', 'Request Date', 'Reason'];
    const rows = data.rows.map((r) => [
      r.employeeCode,
      r.employeeName ?? '',
      r.city ?? '',
      // Raw YYYY-MM-DD in the export, not the display format — spreadsheets can sort
      // and date-parse it, which '12 Mar 2024' defeats.
      r.joiningDate ?? '',
      r.managerName ?? '',
      r.typeLabel,
      r.leaveType ?? '',
      STATUS_LABEL[r.status],
      r.appliedOn ?? '',
      r.referenceDate ?? '',
      r.reason ?? '',
    ]);
    downloadCsv(`Approvals_${data.month.ym}.csv`, buildCsv(headers, rows));
  };

  if (projectId == null) {
    return <div className="flex h-40 items-center justify-center text-sm text-gray-500">Select a project to see its leave and attendance requests.</div>;
  }
  if (loading) {
    return (
      <BlockLoader className="h-72" />
    );
  }
  if (error) return <div className="flex h-40 items-center justify-center text-sm text-rose-600">{error}</div>;
  if (!data) return <div className="flex h-40 items-center justify-center text-sm text-gray-500">No request data.</div>;

  // The month is further back than the upstream regularization source can be
  // queried without timing out. Say so plainly instead of rendering empty tables
  // that would read as "no requests were raised".
  if (!data.supported) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
          <div>
            <h3 className="text-sm font-semibold text-amber-900">{data.month.label} is outside the available history</h3>
            <p className="mt-1 text-xs text-amber-800">{data.notice}</p>
          </div>
        </div>
      </div>
    );
  }

  const k = data.kpis;
  const monthLabel = data.month.label;
  const hasRows = data.rows.length > 0;
  const typeByKey = new Map(data.byType.map((t) => [t.type, t]));
  // The month's list, when the address names one of its four views: the whole month,
  // opened on that status. The KPI cards open it by writing the address.
  const monthStatus = approvalsListFromSlug(list);
  // A roll-up row's requests. The drill hides its manager column when every request
  // shares one direct manager (a manager row, or an AM whose team all report to them) —
  // the title already says who. A Trade Head's requests span several AMs, so theirs keep it.
  const openGroup = (r: ManagerRow, by: 'manager' | 'am' | 'th', status?: StatusFilter) => {
    const codeOf = (x: ApprovalRow): string | null =>
      (by === 'am' ? x.amCode : by === 'th' ? x.thCode : x.managerCode) ?? null;
    const subset = data.rows.filter((x) => codeOf(x) === r.managerCode);
    const hideManager = new Set(subset.map((x) => x.managerCode ?? '')).size <= 1;
    openDrill(r.managerName, subset, { status, hideManager });
  };

  return (
    <div className="space-y-4">
      {/* ─── Header + download ─── */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">Requests overview</h2>
          <p className="text-xs text-gray-500">Leave and regularization requests raised in {monthLabel}</p>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={!hasRows}
          className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-primary-200 bg-white px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" /> Download
        </button>
      </div>

      {/* ─── Incomplete-data banner ─── */}
      {/* Sits ABOVE the numbers, not below them: every figure on this page is an
          undercount when this shows, so the caveat has to be read first. */}
      {data.partial && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <p className="text-xs text-amber-800">
              {data.notice || 'Some data could not be loaded, so these totals are incomplete.'}
            </p>
          </div>
        </div>
      )}

      {/* ─── KPI strip ─── */}
      {/* The four counts open the drill-down on their own status. Approval rate is a
          ratio, not a set of requests, so it stays a plain tile. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        <StatTile label="Requests raised" value={String(k.total)} helper={monthLabel} onClick={k.total > 0 ? () => openList(APPROVALS_LIST.all) : undefined} />
        <StatTile label="Pending" value={String(k.pending)} valueTone={k.pending > 0 ? 'bad' : 'default'} helper="awaiting a decision" onClick={k.pending > 0 ? () => openList(APPROVALS_LIST.pending) : undefined} />
        <StatTile label="Approved" value={String(k.approved)} helper={`of ${k.decided} decided`} onClick={k.approved > 0 ? () => openList(APPROVALS_LIST.approved) : undefined} />
        <StatTile label="Rejected" value={String(k.rejected)} helper={`of ${k.decided} decided`} onClick={k.rejected > 0 ? () => openList(APPROVALS_LIST.rejected) : undefined} />
        <StatTile label="Approval rate" value={pct(k.approvalRate)} valueTone="good" helper="approved ÷ decided" />
      </div>

      {/* ─── By request type ─── */}
      <Card title="By request type" subtitle="Which kind of request is being raised, and how it is decided">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-100">
                {/* Opens in its fixed order (leave, then the two regularization
                    kinds), so the leave → leave-breakdown reading order below holds
                    by default; a third click on a sorted header restores it. */}
                <SortTh label="Request type" sortKey="type" sort={typeSort} className={TH} />
                <SortTh label="Total" sortKey="total" sort={typeSort} align="right" className={THR} />
                <SortTh label="Pending" sortKey="pending" sort={typeSort} align="right" className={THR} />
                <SortTh label="Approved" sortKey="approved" sort={typeSort} align="right" className={THR} />
                <SortTh label="Rejected" sortKey="rejected" sort={typeSort} align="right" className={THR} />
                <SortTh label="Approval rate" sortKey="rate" sort={typeSort} align="right" className={THR} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {typeSort.sorted.map((t) => {
                const open = (status?: StatusFilter) => openDrill(t.label, data.rows.filter((r) => r.type === t.type), { status });
                return (
                  <tr key={t.type} className="cursor-pointer hover:bg-gray-50" onClick={() => open()}>
                    <td className={TD}>{t.label}</td>
                    <td className={`${TDR} font-medium`}>{t.total}</td>
                    <CountCell value={t.pending} className={TDR} onOpen={() => open('pending')} />
                    <CountCell value={t.approved} className={TDR} onOpen={() => open('approved')} />
                    <CountCell value={t.rejected} className={TDR} onOpen={() => open('rejected')} />
                    <td className={TDR}>{pct(t.approvalRate)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Leave sub-types sit under the parent row rather than as their own card —
            they are a decomposition of one number above, not a peer of it. */}
        {leaveRows.length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-3">
            <p className="mb-2 text-xs font-medium text-gray-500">
              Leave broken down by type ({typeByKey.get('leave')?.total ?? 0} requests)
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <SortTh label="Leave type" sortKey="leaveType" sort={leaveSort} className={TH} />
                    <SortTh label="Total" sortKey="total" sort={leaveSort} align="right" className={THR} />
                    <SortTh label="Pending" sortKey="pending" sort={leaveSort} align="right" className={THR} />
                    <SortTh label="Approved" sortKey="approved" sort={leaveSort} align="right" className={THR} />
                    <SortTh label="Rejected" sortKey="rejected" sort={leaveSort} align="right" className={THR} />
                    <SortTh label="Approval rate" sortKey="rate" sort={leaveSort} align="right" className={THR} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {leaveSort.sorted.map((r) => {
                    const open = (status?: StatusFilter) =>
                      openDrill(`Leave — ${r.leaveType}`, data.rows.filter((x) => x.type === 'leave' && normLeaveType(x.leaveType) === r.leaveType), { status });
                    return (
                      <tr key={r.leaveType} className="cursor-pointer hover:bg-gray-50" onClick={() => open()}>
                        <td className={TD}>{r.leaveType}</td>
                        <td className={`${TDR} font-medium`}>{r.total}</td>
                        <CountCell value={r.pending} className={TDR} onOpen={() => open('pending')} />
                        <CountCell value={r.approved} className={TDR} onOpen={() => open('approved')} />
                        <CountCell value={r.rejected} className={TDR} onOpen={() => open('rejected')} />
                        <td className={TDR}>{pct(r.approvalRate)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      {/* ─── By Trade Head, then By Assistant Manager ─── */}
      {/* The same requests counted at two levels of the hierarchy — each Trade Head, and
          the AMs under them — so each table reconciles with the KPI total on its own.
          Stacked full width rather than side by side: with the pending-TAT columns and
          one-line headers, each table is wider than half a laptop screen. Trade Head
          comes first because it is the short summary — the longer AM list follows it,
          the same parent-then-breakdown order as the request-type card above. Only shown
          when the project defines those roles; otherwise By manager (below) stands in. */}
      {tiersOn && (
        <>
          <Card
            title="By Trade Head"
            subtitle="Every Assistant Manager's team under each Trade Head, combined. Click a row, or a count, for the requests."
          >
            {thRows.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">No requests raised in {monthLabel}.</p>
            ) : (
              <ManagerBreakdownTable rows={thRows} nameLabel="Trade Head" onOpen={(r, status) => openGroup(r, 'th', status)} />
            )}
          </Card>
          <Card
            title="By Assistant Manager"
            subtitle="Requests from each Assistant Manager's team, and how they were decided. Click a row, or a count, for the requests."
          >
            {amRows.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">No requests raised in {monthLabel}.</p>
            ) : (
              <ManagerBreakdownTable rows={amRows} nameLabel="Assistant Manager" onOpen={(r, status) => openGroup(r, 'am', status)} />
            )}
          </Card>
        </>
      )}

      {/* ─── By manager (when the project has no AM / Trade Head roles) ─── */}
      {/* Full width for the same reason as the pair above: its eleven columns don't fit
          beside Top requesters, which follows it. */}
      {!tiersOn && (
        <Card
          title="By manager"
          subtitle="Requests landing in each manager's queue, and how they were decided. Click a row, or a count, for the requests."
        >
          {managerRows.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">No requests raised in {monthLabel}.</p>
          ) : (
            <ManagerBreakdownTable rows={managerRows} nameLabel="Manager" onOpen={(r, status) => openGroup(r, 'manager', status)} />
          )}
        </Card>
      )}

      {/* ─── Top requesting employees ─── */}
      <Card
        title="Top requesting employees"
        subtitle="Who raises the most requests. Click a row, or a count, for their requests."
      >
        {requesterRows.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">No requests raised in {monthLabel}.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <SortTh label="Employee" sortKey="emp" sort={empSort} className={TH} />
                  {/* City and Joined sit with Employee/Manager because they are
                      attributes of the person, not another count. Joined sorts on
                      the raw ISO value, so it orders chronologically rather than by
                      the "12 Mar 2024" display string. */}
                  <SortTh label="City" sortKey="city" sort={empSort} className={TH} />
                  <SortTh label="Joined" sortKey="joined" sort={empSort} className={TH} title="Employee joining date" />
                  <SortTh label="Manager" sortKey="manager" sort={empSort} className={TH} />
                  <SortTh label="Total" sortKey="total" sort={empSort} align="right" className={THRC} />
                  <SortTh label="Leave" sortKey="leave" sort={empSort} align="right" className={THRC} />
                  <SortTh label="Att. reg." sortKey="attreg" sort={empSort} align="right" className={THRC} />
                  <SortTh label="Market reg." sortKey="mktreg" sort={empSort} align="right" className={THRC} />
                  <SortTh label="Pending" sortKey="pending" sort={empSort} align="right" className={THRC} />
                  <SortTh label="Approved" sortKey="approved" sort={empSort} align="right" className={THRC} />
                  <SortTh label="Rejected" sortKey="rejected" sort={empSort} align="right" className={THRC} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {empSort.sorted.slice(0, 50).map((r) => {
                  const open = (status?: StatusFilter) =>
                    openDrill(r.employeeName || r.employeeCode, data.rows.filter((x) => x.employeeCode === r.employeeCode), { status });
                  return (
                    <tr key={r.employeeCode} className="cursor-pointer hover:bg-gray-50" onClick={() => open()}>
                      <td className={TD}>
                        <div className="font-medium text-gray-900">{r.employeeName || '—'}</div>
                        <div className="text-[11px] text-gray-400">{r.employeeCode}</div>
                      </td>
                      {/* max-w makes `truncate` actually truncate — a table cell grows
                          to fit otherwise, and one long city or manager name would push
                          the counts off the edge of the card. */}
                      <td className={`${TD} max-w-[95px] truncate`} title={r.city || undefined}>{r.city || '—'}</td>
                      <td className={`${TD} whitespace-nowrap`}>{fmtDate(r.joiningDate)}</td>
                      <td className={`${TD} max-w-[105px] truncate`} title={r.managerName || undefined}>{r.managerName ?? '—'}</td>
                      <td className={`${TDRC} font-medium`}>{r.total}</td>
                      <td className={TDRC}>{r.byType.leave}</td>
                      <td className={TDRC}>{r.byType.attendance_regularization}</td>
                      <td className={TDRC}>{r.byType.market_regularization}</td>
                      <CountCell value={r.pending} className={tonedCell(TDRC, r.pending > 0 && 'font-medium text-amber-700')} onOpen={() => open('pending')} />
                      <CountCell value={r.approved} className={TDRC} onOpen={() => open('approved')} />
                      <CountCell value={r.rejected} className={TDRC} onOpen={() => open('rejected')} />
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {requesterRows.length > 50 && (
              <p className="mt-2 text-[11px] text-gray-400">
                Showing the top 50 of {requesterRows.length} employees. Download for the full list.
              </p>
            )}
          </div>
        )}
      </Card>

      {/* ─── How to read this ─── */}
      {/* These two caveats change what the numbers mean, so they are stated on the
          page rather than left for someone to discover by disagreeing with it. */}
      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
          <div className="text-[11px] leading-relaxed text-gray-500">
            <span className="font-medium text-gray-600">Manager</span> is the employee's current reporting manager —
            the person the request is routed to — not necessarily whoever actioned it. If an employee's manager has
            changed, their earlier requests appear under the new manager.
            {tiersOn && (
              <>
                {' '}<span className="font-medium text-gray-600">Assistant Manager</span> and{' '}
                <span className="font-medium text-gray-600">Trade Head</span> are the nearest people above the employee
                who hold those roles: a Trade Head's row combines every AM team under them, and an AM's own requests
                count under the AM above them, if there is one.
              </>
            )}
            <br />
            <span className="font-medium text-gray-600">{monthLabel}</span> counts requests <em>raised</em> in the month,
            shown with their status as of now. Requests still pending here may be decided later.
            <br />
            <span className="font-medium text-gray-600">TAT</span> is in calendar days — for a pending request, since it
            was applied; for a decided one, from applying to the decision. It shows — where 1Office doesn't send the
            decision date, which it never does for regularizations.{' '}
            <span className="font-medium text-gray-600">Avg / Max pending TAT</span> are across each person's requests
            that are still pending: the average wait, and the longest.
          </div>
        </div>
      </div>

      {/* ─── Drill-down ─── */}
      {/* The month's list is open while the address names it; closing takes the name out
          of the address. Not keyed by its status, so switching a chip keeps the search. */}
      {monthStatus ? (
        <DrillDownModal
          key="month"
          drill={{ id: 0, title: `Requests raised in ${monthLabel}`, rows: data.rows, status: monthStatus }}
          ym={data.month.ym}
          onStatus={openList}
          onClose={closeList}
        />
      ) : (
        drill && <DrillDownModal key={drill.id} drill={drill} ym={data.month.ym} onClose={() => setDrill(null)} />
      )}
    </div>
  );
}
